/**
 * FeaturedInBar — displays REAL (never fabricated) media placements.
 *
 * The component silently returns null when the /api/press/placements
 * endpoint has no active rows — so on a fresh install the landing page
 * shows nothing here, and the bar only "turns on" once you (the admin)
 * insert real placements via:
 *
 *   POST /api/press/placements
 *   Header: X-Admin-Token: $ADMIN_CONFIG_TOKEN
 *   Body: { outlet, outlet_slug, article_title, article_url, logo_url, quote, placement_type }
 *
 * Legal note: fabricating "As Seen in Forbes" without a real Forbes
 * article violates Canada's Competition Act s. 74.01, Forbes' trademark
 * rights, and Google/Meta Ads policies. This component was designed to
 * make it impossible to fake — no hardcoded outlet list, all data comes
 * from the DB.
 */
import { useEffect, useState } from "react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function FeaturedInBar() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/press/placements`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && Array.isArray(j?.items)) setItems(j.items); })
      .catch(() => { /* silent — bar simply hides if endpoint is offline */ });
    return () => { cancelled = true; };
  }, []);

  if (!items.length) return null;

  return (
    <section
      data-testid="featured-in-bar"
      className="border-y border-slate-200 bg-slate-50/70 py-8 md:py-10"
      aria-label="Featured in the press"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 text-center mb-5">
          As featured in
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-5">
          {items.map((p) => (
            <a
              key={p.id}
              href={p.article_url}
              target="_blank"
              rel="noreferrer nofollow"
              title={`${p.outlet} — ${p.article_title}`}
              data-testid={`press-logo-${p.outlet_slug}`}
              className="grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all"
            >
              {p.logo_url ? (
                <img
                  src={p.logo_url}
                  alt={p.outlet}
                  className="h-7 md:h-8 w-auto"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className="text-lg md:text-xl font-serif font-bold text-slate-700">
                  {p.outlet}
                </span>
              )}
            </a>
          ))}
        </div>
        {items[0]?.quote && (
          <blockquote className="mt-6 text-center max-w-3xl mx-auto text-sm md:text-base text-slate-600 italic">
            &ldquo;{items[0].quote}&rdquo;
            <cite className="block mt-2 not-italic text-xs font-semibold text-slate-500 tracking-wide uppercase">
              — {items[0].outlet}
            </cite>
          </blockquote>
        )}
      </div>
    </section>
  );
}
